import { BASE_URL, TOKEN_ERROR_CODE } from './constant/index.js';
import { isString, isFunction, isPositiveInteger, isObject } from './helper/validator.js';
import { updateObject } from './helper/utils.js';
import EventEmitter from './helper/event-emitter.js';
import fetchInstance from './api/fetch.js';

fetchInstance.setBaseUrl(BASE_URL);

function fetchHelper({ retryCount, authRenewCount, authRenewBeforeCallback, webAuth }, apiInstance) {
  let _defaultRetryCount = isPositiveInteger(retryCount) ? retryCount : 0;
  let _defaultAuthRenewCount = isPositiveInteger(authRenewCount) ? authRenewCount : 0;
  let _authRenewBeforeCallback = authRenewBeforeCallback;
  let _webAuth = webAuth;

  return {
    setRetryCount(cnt) {
      if (!isPositiveInteger(cnt)) {
        throw TypeError('The parameter must be a positive integer.');
      }
      _defaultRetryCount = cnt;
    },
    setAuthRenewCount(cnt) {
      if (!isPositiveInteger(cnt)) {
        throw TypeError('The parameter must be a positive integer.');
      }
      _defaultAuthRenewCount = cnt;
    },
    setAuthRenewBeforeCallback(fn) {
      if (!isFunction(fn)) {
        throw TypeError('The parameter must be of a function type.');
      }
      _authRenewBeforeCallback = fn;
    },
    retry(requestData) {
      let _retryCnt = _defaultRetryCount;
      let _authRenewCnt = _defaultAuthRenewCount;

      return {
        async execute(needAuthRenew = true) {
          try {
            const response = await fetchInstance.request(requestData);

            _retryCnt = _defaultRetryCount;
            _authRenewCnt = _defaultAuthRenewCount;

            return response;
          } catch (error) {
            if (_retryCnt > 0) {
              if (needAuthRenew && error?.resultCode === TOKEN_ERROR_CODE && _authRenewCnt > 0) {
                _authRenewCnt--;

                if (isFunction(_authRenewBeforeCallback)) {
                  _webAuth = await _authRenewBeforeCallback(_webAuth);
                }

                try {
                  await apiInstance._fetchToken(_webAuth);
                } catch (error) {
                  return Promise.reject(error);
                }
              }

              _retryCnt--;
              return await this.execute();
            } else {
              _retryCnt = _defaultRetryCount;
              _authRenewCnt = _defaultAuthRenewCount;
              return Promise.reject(error);
            }
          }
        }
      };
    }
  };
}

export default class Collector {
  _userInfo = {
    userId: '',
    examNo: '',
    appKey: ''
  };
  _deviceType = 'pc'; // OR mobile
  _webAuth = null; // userId, token, via
  _accessToken = '';
  _fetchHelper = null;

  constructor({ userInfo, ...config }) {
    this._userInfo = updateObject(this._userInfo, userInfo);
    this._deviceType = config.deviceType;

    this._setWebAuth(config.webAuth);
    this._setFetchOption(config);
    this._setFetchInterceptor();

    this._fetchToken();
  }

  _setWebAuth(webAuth) {
    if (!webAuth.token) throw Error('The webAuth token is required.');

    this._webAuth = updateObject(
      {
        userId: '',
        token: '',
        via: ''
      },
      webAuth
    );
    this._accessToken = webAuth.token;
  }

  _setFetchOption(config) {
    config.apiBaseUrl && fetchInstance.setBaseUrl(config.apiBaseUrl);
    this._fetchHelper = fetchHelper(
      {
        webAuth: this._webAuth,
        retryCount: config.retryCount,
        authRenewCount: config.authRenewCount,
        authRenewBeforeCallback: config.authRenewBeforeCallback
      },
      this
    );
  }

  _setFetchInterceptor() {
    const requestInterceptor = (config) => {
      return config;
    };
    const successInterceptor = (response) => {
      const { header } = response;
      const { isSuccessful, resultCode, resultMessage } = header;

      if (isSuccessful) {
        EventEmitter.fire('api:success', { ...response });
        return response;
      } else {
        EventEmitter.fire('api:fail', { resultCode, resultMessage });
        return Promise.reject({ ...response });
      }
    };
    const failInterceptor = (error) => {
      EventEmitter.fire('api:error', error);
      return Promise.reject(error);
    };

    fetchInstance.interceptors.request(requestInterceptor);
    fetchInstance.interceptors.response(successInterceptor, failInterceptor);
  }

  _setAccessToken(_accessToken) {
    this._accessToken = _accessToken;
  }

  getApiBaseUrl() {
    return fetchInstance.getBaseUrl();
  }

  setApiBaseUrl(apiBaseUrl) {
    fetchInstance.setBaseUrl(apiBaseUrl);
  }

  updateUserInfo(newUserInfo) {
    if (!isObject(newUserInfo)) {
      throw Error('Check user info config.');
    }

    updateObject(this._userInfo, newUserInfo);
  }

  on(event, handler) {
    if (!isString(event)) {
      throw TypeError('The event name must be of a string type.');
    }
    if (!isFunction(handler)) {
      throw TypeError('The handler must be of a function type.');
    }
    EventEmitter.on(event, handler);
  }

  setRetryCount(cnt) {
    this._fetchHelper.setRetryCount(cnt);
  }

  setAuthRenewCount(cnt) {
    this._fetchHelper.setAuthRenewCount(cnt);
  }

  setAuthRenewBeforeCallback(fn) {
    this._fetchHelper.setAuthRenewBeforeCallback(fn);
  }

  /*
    API 
   */
  get _authHeader() {
    return {
      'X-Auth-Token': `Bearer ${this._accessToken}`
    };
  }

  async _fetchToken() {
    const expiresIn = 21600;
    const requestData = {
      userId: this._userInfo.userId,
      deviceType: this._deviceType,
      examNo: this._userInfo.examNo,
      webAuth: this._webAuth
    };

    try {
      const response = await this._fetchHelper
        .retry({
          url: `/auth/token`,
          method: 'post',
          body: requestData,
          params: {
            appKey: this._userInfo.appKey,
            expiresIn
          }
        })
        .execute(false);
      this._setAccessToken(response.data.accessToken);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async revokeAccessToken() {
    try {
      const bodyData = {
        accessToken: this._accessToken
      };

      return await this._fetchHelper
        .retry({
          url: '/auth/revoke',
          method: 'post',
          body: bodyData
        })
        .execute(false);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * 측면 카메라 사전 검증 API
   * @param {{file:FormData}} bodyData
   * @returns {Promise} API요청 결과 반환
   */
  async preCheckSideCamera(bodyData) {
    const { appKey } = this._userInfo;

    try {
      return await this._fetchHelper
        .retry({
          url: `/nhn-pre-chk/v1.0/appkeys/${appKey}/side`,
          method: 'post',
          body: bodyData,
          headers: this._authHeader
        })
        .execute();
    } catch (error) {
      if (error?.resultCode === TOKEN_ERROR_CODE) {
        return Promise.reject({ ...error, methodName: 'preCheckSideCamera' });
      } else {
        return Promise.reject(error);
      }
    }
  }

  /**
   * 가운데 시선 정보 등록 API
   * @param {{file:FormData}} bodyData
   * @returns {Promise} API요청 결과 반환
   */
  async registerBehavior(bodyData) {
    const { userId, examNo, appKey } = this._userInfo;

    try {
      return await this._fetchHelper
        .retry({
          url: `/nhn-behavior-reg/v1.0/appkeys/${appKey}/exam/${examNo}/users/${userId}/gaze`,
          method: 'post',
          body: bodyData,
          headers: this._authHeader
        })
        .execute();
    } catch (error) {
      if (error?.resultCode === TOKEN_ERROR_CODE) {
        return Promise.reject({ ...error, methodName: 'registerBehavior' });
      } else {
        return Promise.reject(error);
      }
    }
  }

  /**
   * 얼굴 인식 API
   * @param {{image: { url: string, bytes: Array<Uint8Array> }}} bodyData
   * @returns {Promise} API요청 결과 반환
   */
  async fetchFaceDetect(bodyData) {
    try {
      return await this._fetchHelper
        .retry({
          url: `/nhn-face-reco/v1.0/appkeys/${this._userInfo.appKey}/detect`,
          method: 'post',
          body: bodyData
        })
        .execute();
    } catch (error) {
      if (error?.resultCode === TOKEN_ERROR_CODE) {
        return Promise.reject({ ...error, methodName: 'fetchFaceDetect' });
      } else {
        return Promise.reject(error);
      }
    }
  }

  /**
   * 행동 감지 요청 API
   * @param {{file:FormData}} bodyData
   * @param {{camLocation:string, reqTime:number}} queryParams { camLocation : 카메라 위치 정보(side(측면), front(정면)), reqTime: 요청 시간(timestamp 10자리)(초 단위까지)	}
   * @returns {Promise} API요청 결과 반환
   */
  async fetchBehaviorDetect(bodyData, { camLocation, reqTime }) {
    const { userId, examNo, appKey } = this._userInfo;

    const queryParams = {
      reqTime,
      camLocation
    };
    try {
      return await this._fetchHelper
        .retry({
          url: `/nhn-behavior-det/v1.0/appkeys/${appKey}/exam/${examNo}/users/${userId}`,
          method: 'post',
          body: bodyData,
          params: queryParams,
          headers: this._authHeader
        })
        .execute();
    } catch (error) {
      if (error?.resultCode === TOKEN_ERROR_CODE) {
        return Promise.reject({ ...error, methodName: 'fetchVoiceDetect' });
      } else {
        return Promise.reject(error);
      }
    }
  }

  /**
   * 음성 감지 요청 API
   * @param {{file: BinaryData}} bodyData
   * @param {reqTime: number} queryParams { reqTime: 요청 시간(timestamp 10자리)(초 단위까지) }
   * @returns {Promise} API요청 결과 반환
   */
  async fetchVoiceDetect(bodyData, { reqTime }) {
    const { userId, examNo, appKey } = this._userInfo;

    try {
      const queryParams = {
        reqTime
      };
      return await this._fetchHelper
        .retry({
          url: `/nhn-voice-det/v1.0/appkeys/${appKey}/exam/${examNo}/users/${userId}`,
          method: 'post',
          body: bodyData,
          params: queryParams,
          headers: this._authHeader
        })
        .execute();
    } catch (error) {
      if (error?.resultCode === TOKEN_ERROR_CODE) {
        return Promise.reject({ ...error, methodName: 'fetchVoiceDetect' });
      } else {
        return Promise.reject(error);
      }
    }
  }
}
